'use client';

import { forwardRef, useCallback, useContext, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { ChipGroupContext } from '../chip-group/context/chip-group.context';
import { InputChipContext } from './context/input-chip.context';
import { inputChipRootRecipe } from './styles/input-chip-root.styles';
import type { InputChipRootPropsInterface } from './types/input-chip-root.types';

/**
 * @since 10.0.0
 */
export const InputChipRoot = forwardRef<HTMLSpanElement, InputChipRootPropsInterface>(
  (props: InputChipRootPropsInterface, forwardedRef) => {
    const chipGroupContext = useContext(ChipGroupContext);

    const {
      children,
      elevated = chipGroupContext.elevated || false,
      className,
      css: cssProp,
      disabled = chipGroupContext.disabled || false,
      isOpen,
      onClose,
      ...rest
    } = props;

    const styles = css(inputChipRootRecipe.raw({ elevated, disabled }), cssProp);

    const [openState, setOpenState] = useControllableValue<boolean | undefined>({
      controlledValue: isOpen,
      uncontrolledValue: true,
      isControlled: 'isOpen' in props,
      isUncontrolled: false,
      componentName: 'InputChip.Root',
      controlledPropName: 'isOpen',
    });

    const [labelText, setLabelText] = useState('');

    const handleClose = useCallback(() => {
      setOpenState(false);
      onClose?.();
    }, [onClose, setOpenState]);

    const contextValue = useMemo(
      () => ({ disabled, handleClose, labelText, setLabelText }),
      [disabled, handleClose, labelText, setLabelText],
    );

    if (!openState) {
      return null;
    }

    return (
      <InputChipContext.Provider value={contextValue}>
        <span
          className={cx('c11n', styles, className)}
          data-c11n-component="InputChip.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          {children}
        </span>
      </InputChipContext.Provider>
    );
  },
);

InputChipRoot.displayName = 'InputChip.Root';
